import type { Source } from 'src/pages/reports/common/consts';
import type { TFilters } from 'src/providers/FiltersProvider';

import { z as zod } from 'zod';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import MenuItem from '@mui/material/MenuItem';
import { Box, Button, Divider, Typography } from '@mui/material';

import { REPORT_SOURCES, REPORT_CUSTOMER_TYPES } from 'src/pages/reports/common/consts';

import { Form, Field } from 'src/components/hook-form';

import { Iconify } from '../iconify';

type TFilterFormSchema = zod.infer<typeof FilterFormSchema>;

export const FilterFormSchema = zod
  .object({
    locationIds: zod.array(zod.string()).optional(),
    dateFrom: zod.string().optional(),
    dateTo: zod.string().optional(),
    source: zod.string().optional(),
    customerType: zod.string().optional(),
    threshold: zod.number({ coerce: true }).optional(),
    repeatType: zod.string().optional(),
  })
  .refine((data) => (data.dateFrom && data.dateTo ? data.dateFrom <= data.dateTo : true), {
    message: 'Start date must be less than end date',
    path: ['dateFrom'],
  });

type FilterFormProps = {
  currentFilters: TFilters;
  onUpdateFilters: (value: Partial<TFilters>) => void;
  onResetFilters: () => void;
  allowedSources?: Source[];
  allowFilterByStore?: boolean;
  allowedCustomerTypes?: string[];
  allowFilterByDate?: boolean;
  allowFilterByThreshold?: boolean;
  allowFilterByRepeatType?: boolean;
};

export function FilterForm({
  currentFilters,
  onUpdateFilters,
  onResetFilters,
  allowedSources = [],
  allowedCustomerTypes = [],
  allowFilterByStore = true,
  allowFilterByDate = true,
  allowFilterByThreshold = false,
  allowFilterByRepeatType = false,
}: FilterFormProps) {
  const methods = useForm<TFilterFormSchema>({
    mode: 'onSubmit',
    resolver: zodResolver(FilterFormSchema),
    values: currentFilters,
  });

  const { handleSubmit } = methods;

  const onSubmit = handleSubmit((values: TFilterFormSchema) => {
    onUpdateFilters(values as Partial<TFilters>);
  });

  const onReset = useCallback(() => {
    onResetFilters();
  }, [onResetFilters]);

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <Box display="flex" flexDirection="column" flex={1} justifyContent="flex-start" gap={2}>
        <Typography variant="subtitle2">Filter by data source</Typography>

        <Field.Select name="source" label="Source">
          {REPORT_SOURCES.filter((source) => {
            if (allowedSources.length) {
              return allowedSources.includes(source.value as Source);
            }

            return true;
          }).map((source) => (
            <MenuItem key={source.value} value={source.value}>
              {source.label}
            </MenuItem>
          ))}
        </Field.Select>

        {allowedCustomerTypes.length ? (
          <>
            <Typography variant="subtitle2">Filter by customer type</Typography>

            <Field.Select name="customerType" label="Customer type">
              {REPORT_CUSTOMER_TYPES.filter((customerType) => {
                if (allowedCustomerTypes.length) {
                  return allowedCustomerTypes.includes(customerType.value as Source);
                }

                return true;
              }).map((customerType) => (
                <MenuItem key={customerType.value} value={customerType.value}>
                  {customerType.label}
                </MenuItem>
              ))}
            </Field.Select>
          </>
        ) : null}

        {allowFilterByDate ? (
          <>
            <Typography variant="subtitle2">Filter by date range</Typography>

            <Field.DatePicker
              valueAsString
              format="MM/DD/YYYY"
              name="dateFrom"
              label="Start date (ET)"
            />
            <Field.DatePicker
              valueAsString
              format="MM/DD/YYYY"
              name="dateTo"
              label="End date (ET)"
            />
            {allowFilterByStore ? (
              <>
                <Typography variant="subtitle2">Filter by stores</Typography>

                <Field.Locations
                  includeInactiveLocations
                  fullWidth
                  name="locationIds"
                  label="Select stores"
                />
              </>
            ) : null}

            <Divider sx={{ borderStyle: 'dashed', width: '100%' }} />
          </>
        ) : null}

        {allowFilterByThreshold ? (
          <>
            <Typography variant="subtitle2">Filter by threshold</Typography>
            <Field.Text type="number" name="threshold" label="Order threshold" />
          </>
        ) : null}

        {allowFilterByRepeatType ? (
          <>
            <Typography variant="subtitle2">Filter by repeat type</Typography>
            <Field.Select name="repeatType" label="Repeat type">
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
            </Field.Select>
          </>
        ) : null}

        <Button
          variant="contained"
          onClick={onSubmit}
          fullWidth
          startIcon={<Iconify icon="solar:round-double-alt-arrow-right-bold" />}
        >
          Apply filters
        </Button>
        <Button
          fullWidth
          color="error"
          variant="outlined"
          onClick={onReset}
          startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
        >
          Reset filters
        </Button>
      </Box>
    </Form>
  );
}
